import { Card, Metadata } from "@dropbox/dash-component-library";
import { Text } from "@dropbox/dig-components/typography";
import type { SourceRowParts } from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import { getSourceRowParts } from "@mirage/mosaics/Chat/components/compose-sources/ComposeSourceRow";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { memo } from "react";
import styles from "./AnswerCitationCardShowMoreCard.module.css";

import { List } from "@dropbox/dig-components/list";
import { UIIcon } from "@dropbox/dig-icons";
import { ChevronRightLine } from "@dropbox/dig-icons/dist/mjs/assets";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { AnswerCitationCardsInOverlay } from "./AnswerCitationCardsInOverlay";

interface AnswerCitationShowMoreCardProps {
  moreSources: ChatSource[];
  onClick: () => void;
  conversationId?: string;
  searchSessionId?: string;
  assistRequestId?: string;
  listItem?: boolean;
}
export const AnswerCitationShowMoreCard = memo(
  ({
    moreSources,
    onClick,
    conversationId,
    searchSessionId,
    assistRequestId,
    listItem,
  }: AnswerCitationShowMoreCardProps) => {
    const sourcesRowParts: SourceRowParts[] = [];
    for (const source of moreSources) {
      const sourceRowParts = getSourceRowParts(source, {
        icon: {
          hasBackground: false,
          size: "small",
        },
      });
      if (sourceRowParts) {
        sourcesRowParts.push(sourceRowParts);
      }
    }
    return (
      <AnswerCitationCardsInOverlay
        placement={"bottom-end"}
        sources={moreSources}
        listView={listItem}
        refComponent={
          listItem ? (
            <List.Item
              onClick={onClick}
              spacing="small"
              paddingLeft={0}
              paddingRight={0}
              className={classNames(
                styles.AnswersCursorPointer,
                styles.AnswerCitationListItem,
              )}
            >
              <List.Accessory className={styles.AnswerCitationListAccessory}>
                <Metadata
                  size="small"
                  withDividers="blank"
                  className={styles.AnswerCitationCardMetadata}
                >
                  {Array.from(
                    new Map(
                      sourcesRowParts
                        .slice(0, 5)
                        .map((item) => [
                          item.mirageResult.connectorInfo.connectorId,
                          item,
                        ]),
                    ).values(),
                  ).map((sourcesRowPart, i) => (
                    <Metadata.Item key={i}>{sourcesRowPart.icon}</Metadata.Item>
                  ))}
                </Metadata>
              </List.Accessory>
              <List.Content className={styles.AnswerCitationListContent}>
                <Text
                  color="subtle"
                  size="medium"
                  className={styles.AnswerCitationsSeeMoreList}
                >
                  <span>
                    {i18n.t("answers_see_more_citations", {
                      count: moreSources.length,
                    })}
                  </span>
                  <UIIcon src={ChevronRightLine} color="subtle" />
                </Text>
              </List.Content>
            </List.Item>
          ) : (
            <Card
              variant="outline"
              isLink
              className={styles.AnswerCitationCard}
              style={{
                padding: "var(--dig-spacing__micro__medium) 4px", // Explicitly apply padding to override parent padding
              }}
            >
              <Card.Contents breakout="large">
                <Card.OverlayLink onClick={onClick} href={"#"}>
                  <Text
                    size="medium"
                    className={styles.AnswerCitationShowMoreCardTitle}
                  >
                    {i18n.t("answers_see_more_citations", {
                      count: moreSources.length,
                    })}
                  </Text>
                </Card.OverlayLink>
              </Card.Contents>
              <Card.Contents breakout="large">
                <Metadata
                  size="small"
                  withDividers="blank"
                  className={styles.AnswerCitationCardMetadata}
                >
                  {Array.from(
                    new Map(
                      sourcesRowParts
                        .slice(0, 5)
                        .map((item) => [
                          item.mirageResult.connectorInfo.connectorId,
                          item,
                        ]),
                    ).values(),
                  ).map((sourcesRowPart, i) => (
                    <Metadata.Item key={i}>{sourcesRowPart.icon}</Metadata.Item>
                  ))}
                </Metadata>
              </Card.Contents>
            </Card>
          )
        }
        conversationId={conversationId}
        searchSessionId={searchSessionId}
        assistRequestId={assistRequestId}
      />
    );
  },
);
AnswerCitationShowMoreCard.displayName = "AnswerCitationShowMoreCard";
